<template>
	<div class="offerPosition">
		<div>
			<cad-preview
				:articleId="position.articleId"
				width="100px"
				height="100px"
				:readonly="true"
			></cad-preview>
			<download-cad-component :translate="true" :articleId="position.articleId"></download-cad-component>
		</div>
		<div class="properties">
			<strong> Blexon Id: </strong>
			<span>{{ position.articleId }}</span
			><br />
			<strong> LxBxH: </strong>
			<span>
				{{ article.length | formatDigit }}x<br />{{ article.width | formatDigit }}x<br />{{
					article.height | formatDigit
				}}
				mm
			</span>
			<br />
			<strong> Gewicht </strong>
			<span> {{ (article.weight * position.quantity) | formatDigit }} kg<br /> </span> <br />
			<strong> Menge: </strong>
			<span>{{ position.quantity }} Stk</span><br />
			<strong> Total: </strong>
			<span> {{ (position.quantity * getFertigerPrice()) | formatDigit }} </span><br />
			<strong> Materialanteil: </strong>
			<span>{{ getMaterialPrice() | formatDigit }}</span
			><br />

			<price-text-box
				label="Gesamtpositionspreis (ohne Material)"
				:readonly="offerState !== 0"
				:value="newTotal"
				:errorMessages="errorMessages"
				@valueChanged="updateTotal($event)"
			>
			</price-text-box>
		</div>
		<div>
			{{ getEntgratartAsText(article.entgratart) }}
			<span v-if="article.hasEngravings">
				<br />
				Gravur
			</span>
			<span v-for="(feature, index) in article.featureDtos" :key="'f' + index">
				<br />
				{{ feature.count }} x {{ feature.friendlyName }}
			</span>
			<span v-for="(weldline, index) in article.weldlines" :key="'w' + index">
				<br />
				{{ weldline.length | formatDigit }} mm Schweißnaht |
				{{ weldline.clean ? "verputzt" : "unverputzt" }}
			</span>
			<span v-if="article.powderCoatingTreatment">
				<br />
				<span
					:style="
						'display:inline-block; height: 10px; width: 10px; background-color:' +
						article.powderCoatingTreatment.powder.surfaceColor.hexCode
					"
				></span>
				&nbsp;RAL {{ article.powderCoatingTreatment.powder.surfaceColor.ralCode }} |
				&nbsp;{{ getStructureText(article.powderCoatingTreatment.powder.structure) }} |
				&nbsp;{{ getFinishText(article.powderCoatingTreatment.powder.finish) }}
				<span v-if="article.powderCoatingTreatment.hasBaseCoating"
					>&nbsp;|&nbsp;vorgrundiert</span
				>
			</span>
			<span v-if="article.zincCoating > 0">
				<br />
				{{ getZincText(article.zincCoating) }}
			</span>
			<span v-if="article.certificateTypes > 0">
				<br />
				<span> Zertifikat 3.1 </span>
			</span>
		</div>
		<div v-if="canSeeDetails()">
			<div v-for="workstep of worksteps" :key="workstep.name" class="priceTable">
				<price-text-box
					:label="workstep.name"
					:readonly="true"
					:value="getCurrentPrice(workstep)"
					@valueChanged="updateCurrentPrice(workstep, $event)"
				>
				</price-text-box>
				<v-text-field
					style="height: 40px"
					v-if="workstep.materialPrice > 0"
					label="Plus Materialkosten"
					:value="(workstep.materialPrice * workstep.quantity) | formatDigit"
					:disabled="true"
				>
				</v-text-field>
				<div>
					<span v-if="workstep.duration.split('.')[0] !== '00:00:00'">
						{{ workstep.duration.split(".")[0] }}
					</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import httpClient from "../store/httpClient";
import CadPreview from "./cadPreviewComponent.vue";
import PriceTextBox from "./priceTextBoxComponent.vue";
import TimespanTextBox from "./timespanTextBoxComponent.vue";
import DownloadCadComponent from "./downloadCadComponent.vue";
import { translateEntgratartToText } from "../store/types";
import { getZincCoatingText } from "@/dtos/zincCoating.js";

export default {
	components: {
		"cad-preview": CadPreview,
		"price-text-box": PriceTextBox,
		"timespan-text-box": TimespanTextBox,
		"download-cad-component": DownloadCadComponent,
	},
	props: {
		position: {},
		worksteps: [],
		offerState: 0,
	},
	data() {
		return {
			article: {},
			newTotal: null,
			errorMessages: [],
		};
	},
	watch: {
		worksteps() {
			this.newTotal =
				Math.round(
					(this.position.quantity * this.getFertigerPrice() - this.getMaterialPrice()) *
						100
				) / 100;
		},
	},
	mounted() {
		this.loadArticle();
	},
	methods: {
		async updateTotal(newPrice) {
			const total = this.getFertigerPrice() * this.position.quantity;
			const quantity = this.position.quantity;
			const ncErzeugungStep = this.worksteps.filter((w) => w.workStepType === 20)[0];
			const ncErzeugung = ncErzeugungStep.manufacturerPrice * quantity;
			const einrichtgebuehrStep = this.worksteps.filter((w) => w.workStepType === 30)[0];
			const einrichtgebuehr = einrichtgebuehrStep.manufacturerPrice * quantity;
			const verpackenStep = this.worksteps.filter((w) => w.workStepType === 160);
			let verpacken = 0;
			if (verpackenStep !== undefined) {
				verpacken = verpackenStep[0].manufacturerPrice * quantity;
			}

			const materialPrice = this.getMaterialPrice();
			if (newPrice - ncErzeugung - einrichtgebuehr - verpacken < 1) {
				this.errorMessages.push("Ungültige Preisangabe");
				return;
			}
			this.errorMessages = [];
			this.newTotal = newPrice;
			const totalWithoutFixCost =
				total - ncErzeugung - einrichtgebuehr - verpacken - materialPrice;
			const newTotalWithoutFixCost =
				this.newTotal - ncErzeugung - einrichtgebuehr - verpacken;

			for (const workstep of this.worksteps) {
				if (
					workstep.workStepType === 20 ||
					workstep.workStepType === 30 ||
					workstep.workStepType === 160
				) {
					continue;
				}
				const prozentual =
					((workstep.manufacturerPrice - workstep.materialPrice) * quantity) /
					totalWithoutFixCost;
				const newStepCost = newTotalWithoutFixCost * prozentual;
				workstep.manufacturerPrice = newStepCost / quantity + workstep.materialPrice;
				await this.updateCurrentPrice(workstep, newStepCost);
			}
			this.loadWorksteps();
		},
		getMaterialPrice() {
			return this.worksteps.reduce(
				(partialSum, w) => partialSum + w.materialPrice * this.position.quantity,
				0
			);
		},
		async loadArticle() {
			try {
				const response = await httpClient().get(
					`api/Artikel/GetArticleById?blexonArtikelId=${this.position.articleId}`
				);
				this.article = response.data;
				console.log(this.article)
			} catch (err) {
				console.log(err);
			}
		},
		getFertigerPrice() {
			return this.worksteps.reduce((partialSum, a) => partialSum + a.manufacturerPrice, 0);
		},

		getCurrentPrice(workstep) {
			return (workstep.manufacturerPrice - workstep.materialPrice) * workstep.quantity;
		},

		loadWorksteps() {
			this.$emit("updateWorksteps");
		},
		async updateCurrentPrice(workstep, newValue) {
			if (newValue != null) {
				newValue /= workstep.quantity;
			}
			try {
				await httpClient().post(
					`api/Offer/UpdatePrice?calculationId=${this.$route.params.calculationId}&` +
						`articleId=${workstep.articleId}&` +
						`workStepType=${workstep.workStepType}&` +
						`newPrice=${newValue}`
				);
			} catch (err) {
				console.log(err);
			} finally {
				this.$emit("loading", false);
			}
		},

		async updateCurrentTime(position, timeOverride, newValue, parameter) {
			timeOverride.setByManufacturer = newValue;
			try {
				await httpClient().post(
					`api/Offer/UpdateTime?calculationId=${this.$route.params.calculationId}&` +
						`articleId=${position.articleId}&` +
						`overrideParameter=${parameter}&` +
						`timeSpan=${newValue}`
				);
				await this.loadWorksteps();
			} catch (err) {
				console.log(err);
			} finally {
				this.$emit("loading", false);
			}
		},

		canSeeDetails() {
			return this.$store.getters.currentFertiger(this.$route.params.fertigerId)
				.canManuallyTransmit;
		},
		getEntgratartAsText(entgratart) {
			return translateEntgratartToText(entgratart);
		},
		getStructureText(structure) {
			if (structure === 0) {
				return "Fein";
			}
			if (structure === 1) {
				return "Grob";
			}
			if (structure === 2) {
				return "Glatt";
			}
			return "undefiniert";
		},
		getFinishText(finish) {
			if (finish === 0) {
				return "Matt";
			}
			if (finish === 1) {
				return "Glänzend";
			}
			return "undefiniert";
		},
		getZincText(zincCoating) {
			return getZincCoatingText(zincCoating);
		},
	},
};
</script>

<style scoped>
.offerPosition {
	display: flex;
}

.offerPosition > div {
	padding: 1em;
}

.properties {
	width: 400px;
}

.properties > strong {
	vertical-align: top;
	text-align: right;
	display: inline-block;
	width: 100px;
}

.properties > span {
	text-align: right;
	display: inline-block;
	width: 150px;
}

.priceTable {
	display: flex;
	text-align: right;
	margin-bottom: 10px;
}

.priceTable > div {
	min-width: 100px;
	align-self: end;
}
</style>
